import React, { useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { DNABox, DNAButton, DNAButtonGroup, DNAContextMenu, DNADivider, DNAText, Iffy } from '@alucio/lux-ui';
import { ScheduleDocumentTooltip, DNAPopover } from 'src/components/DNA';
import AIDisclaimer from 'src/components/Compliance/AIDisclaimer';
import useMachineSelector, { composite } from 'src/hooks/useSelector';
import * as publisherVersioningSelector from 'src/state/machines/publisherVersioning/publisherVersioning.selectors';
import { usePublisherVersioningState } from 'src/state/machines/publisherVersioning/PublisherVersioningProvider';
import { TabOptions } from 'src/state/machines/publisherVersioning/publisherVersioning.types';

export const S = StyleSheet.create({
  readOnlyFooterContainer: {
    padding: 16,
  },
  editModeFooterContainer: {
    margin: 12,
  },
})

const FooterReadOnlyMode: React.FC = () => {
  const { service, currentDocumentVersionORM, handleCancelSchedulePublish } = usePublisherVersioningState();
  const cond = useMachineSelector(
    service,
    (state) => composite(
      state,
      publisherVersioningSelector.isScheduledViewMode,
    ),
  )
  if (!currentDocumentVersionORM) return null;

  const scheduledPublish = currentDocumentVersionORM.meta.schedule.publish.scheduledAt;

  return (
    <>
      <DNADivider />
      <DNABox spacing="between" style={S.readOnlyFooterContainer}>
        {/* READ ONLY MESSAGING */}
        <DNABox spacing="xs">
          <DNAText bold status="warning">
            This is view-only. You can only make edits when versioning up.
          </DNAText>
        </DNABox>
        {/* SCHEDULING MESSAGING */}
        <Iffy is={cond.isScheduledViewMode}>
          <DNABox spacing="sm" alignY="center">
            <ScheduleDocumentTooltip scheduledPublish={scheduledPublish}/>
            <DNAButton
              testID="cancel-schedule-button"
              status="tertiary"
              appearance="outline"
              onPress={handleCancelSchedulePublish}
            >
              Cancel scheduled publish
            </DNAButton>
          </DNABox>
        </Iffy>
      </DNABox>
    </>
  )
}

const FooterEditMode: React.FC = () => {
  const {
    service,
    navigateToPreviousTab,
    navigateToNextTab,
    handlePublish,
    handleSchedulePublish,
  } = usePublisherVersioningState();
  const cond = useMachineSelector(
    service,
    (state) => composite(
      state,
      publisherVersioningSelector.availabletabs,
      publisherVersioningSelector.isInDraftingState,
      publisherVersioningSelector.selectedTabIndex,
      publisherVersioningSelector.isPublishing,
      publisherVersioningSelector.isProcessingNewVersion,
      publisherVersioningSelector.hasFileTypeChangedError,
    ),
  )

  const isFirstTab = useMemo(() => cond.selectedTabIndex === 0, [cond.selectedTabIndex])
  const isLastTab = useMemo(
    () => cond.selectedTabIndex === cond.availabletabs.length - 1,
    [cond.selectedTabIndex, cond.availabletabs],
  )
  const isMatchSlidesTab = useMemo(() => {
    const currentTab = cond.availabletabs?.[cond.selectedTabIndex];
    return currentTab === TabOptions.DOCUMENT_MATCH_SLIDES_TAB;
  }, [cond.availabletabs, cond.selectedTabIndex]);

  return (
    <>
      <DNADivider />
      <DNABox spacing="between" style={S.editModeFooterContainer}>
        <DNABox>
          <Iffy is={!isFirstTab}>
            <DNAButton
              onPress={navigateToPreviousTab}
              disabled={cond.isPublishing}
            >
              Back
            </DNAButton>
          </Iffy>
        </DNABox>

        <DNABox alignY="center" spacing="sm">
          <Iffy is={isMatchSlidesTab && !isLastTab}>
            <DNABox style={{ marginRight: 16 }}>
              <AIDisclaimer />
            </DNABox>
          </Iffy>
          <Iffy is={!isLastTab}>
            <DNAButton
              onPress={navigateToNextTab}
              disabled={cond.isProcessingNewVersion}
            >
              Next
            </DNAButton>
          </Iffy>
          <Iffy is={isLastTab}>
            <DNABox spacing="md" alignY="center">
              <DNAButtonGroup>
                {cond.hasFileTypeChangedError ? (
                  <DNAPopover placement="top" disablePopover={['tabletPWA']}>
                    <DNAPopover.Anchor>
                      <DNAButton
                        testID="publish-button"
                        onPress={handlePublish}
                        disabled={true}
                      >
                        Publish
                      </DNAButton>
                    </DNAPopover.Anchor>
                    <DNAPopover.Content>
                      <DNAText colorMode="dark">File type may not change</DNAText>
                    </DNAPopover.Content>
                  </DNAPopover>
                ) : (
                  <DNAButton
                    testID="publish-button"
                    onPress={handlePublish}
                    disabled={cond.isPublishing}
                  >
                    Publish
                  </DNAButton>
                )}
                <DNAContextMenu>
                  <DNAContextMenu.Anchor>
                    <DNAButton
                      testID="context-drop-down"
                      iconLeft="caret-down"
                      onPress={() => { }}
                      disabled={cond.isPublishing || cond.hasFileTypeChangedError}
                    />
                  </DNAContextMenu.Anchor>
                  <DNAContextMenu.Items>
                    <DNAContextMenu.Item
                      collapseOnPress
                      delay={100}
                      title="Schedule publish"
                      onPress={handleSchedulePublish}
                    />
                  </DNAContextMenu.Items>
                </DNAContextMenu>
              </DNAButtonGroup>
            </DNABox>
          </Iffy>
        </DNABox>
      </DNABox>
    </>
  )
}

const Footer: React.FC = () => {
  const { service, isCriticalError } = usePublisherVersioningState();
  const cond = useMachineSelector(
    service,
    (state) => composite(
      state,
      publisherVersioningSelector.isInDraftingState,
    ),
  )

  if (!cond.isInDraftingState) return <FooterReadOnlyMode />
  else if (cond.isInDraftingState && !isCriticalError) return <FooterEditMode />
  else return null
}

export default Footer
